<template>
  <my-giving-layout>
    <!-- Success animation overlay -->
    <div v-if="showSuccessAnimation" class="fixed inset-0 z-[100] pointer-events-auto">
      <SuccessAnimation
        :autostart="autostartAnimation"
        :amount="selectData.givingAmount"
        :transaction-id="transactionIdToRedirect"
        :spinner-position="spinnerPosition"
      />
    </div>

    <main v-if="isLoading">
      <div class="w-full h-full min-h-screen bg-[#F2EFED] relative">
        <Loading />
      </div>
    </main>

    <PinOverlay
      v-if="!isChecking && showPinOverlay && !hideOverlay"
      :open="true"
      :mode="overlayMode"
      :variant="'local-transfer-egiro'"
      :locked-until="lockedUntil"
      @verify="onPinVerify"
      @forgot="goToForgotPin"
      @cancel="back"
      @lock-expired="handleLockExpired"
      ref="pinOverlay"
    />

    <div class="w-full min-h-screen bg-[#F6F4F0]" v-if="!isLoading || isFastTransfer">
      <div
        class="w-full px-[20px] md:px-[24px] lg:px-[28px] mb-[16px] flex justify-between items-center"
      >
        <BackButton :customBack="back" />
        <div
          v-if="isEdit"
          class="delete-button-container w-full flex justify-end"
          :class="{ 'delete-button-container--ios': isIos }"
        >
          <img
            src="/assets/icons/board/delete.svg"
            alt="Delete"
            class="w-[20px] h-[20px] cursor-pointer"
            @click="showDeleteModal = true"
          />
        </div>
      </div>

      <div class="w-full">
        <InfoModal :is-open="showInfoModal" @close="showInfoModal = false" />
        <ImportantNote
          :is-open="showImportantNote"
          @close="showImportantNote = false"
          :recurring-giving-select="selectData.recurringGivingSelect"
        />

        <!-- Confirmation Modal -->
        <Modal v-model="showConfirmationModal" @update:modelValue="cancelSaveChanges">
          <template #header> Save changes to recurring giving? </template>

          <div class="text-[1rem] leading-[1.5rem] font-medium text-[#2B2926]">
            ⚠️ Removal & edits on the day of scheduled transactions might still go through.
          </div>

          <template #footer>
            <div class="flex gap-[16px] w-full">
              <button @click="confirmSaveChanges" class="confirmation-button confirm">
                Confirm
              </button>
              <button @click="cancelSaveChanges" class="confirmation-button cancel">Cancel</button>
            </div>
          </template>
        </Modal>

        <SuccessModal
          :is-open="showSuccessModal"
          @close="showSuccessModal = false"
          :egiro-transaction-status="transactionStatus"
          :recurring-giving-select="selectData.recurringGivingSelect"
          :transaction-details="transactionDetails"
          :edit-mode="isEdit"
          :update-action="updateAction"
          @reset-transaction-data="handleReset"
          @navigate="onSuccessModalNavigate"
        />

        <Modal v-model="showDeleteModal" :show-close="false">
          <template #header>Remove recurring giving?</template>

          <div class="text-[1rem] leading-[1.5rem] font-medium text-[#2B2926]">
            ⚠ Scheduled transactions might still go through if removal or changes are made on the
            day of scheduled transactions.
          </div>

          <template #footer>
            <div class="flex gap-[16px] w-full">
              <button @click="confirmDelete" class="confirmation-button confirm">Confirm</button>
              <button @click="showDeleteModal = false" class="confirmation-button cancel">
                Cancel
              </button>
            </div>
          </template>
        </Modal>

        <div class="flex flex-col px-[20px] md:px-[24px] lg:px-[28px]">
          <div
            v-if="isEdit"
            class="pt-[24px] text-[2rem] md:text-[3.25rem] lg:text-[3.5rem] leading-[2.5rem] md:leading-[3.75rem] lg:leading-[4rem] font-[900] text-[#21201E]"
          >
            Manage Recurring Giving
          </div>

          <div v-if="showSelect" class="flex flex-col mt-[16px]">
            <ion-chip
              v-if="isFromGivenNow"
              class="w-fit font-semibold text-center text-[#6D5A48] text-[0.75rem] md:text-[1.375rem] lg:text-[1.5rem] mx-0"
            >
              Recurring Giving
            </ion-chip>

            <div class="flex flex-row justify-between">
              <div
                class="pt-[16px] text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.125rem]"
              >
                Giving to
              </div>
              <div
                class="pt-[16px] text-[1rem] md:text-[1.75rem] lg:text-[1.875rem] leading-[1.5rem] md:leading-[2.375rem] lg:leading-[2.5rem] font-bold"
              >
                {{ selectedGiveType }}
              </div>
            </div>

            <div class="flex flex-row justify-between">
              <div
                class="pt-[16px] text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.125rem]"
              >
                Giving Method
              </div>
              <div
                class="pt-[16px] text-[1rem] md:text-[1.75rem] lg:text-[1.875rem] leading-[1.5rem] md:leading-[2.375rem] lg:leading-[2.5rem] font-bold text-end"
              >
                {{ getBankShortName(selectedBankCode) }} Account
                {{ formatAccountNumber(selectedLinkedAccount) }}
              </div>
            </div>
          </div>

          <div
            class="pt-[24px] text-[1.5rem] md:text-[2.25rem] lg:text-[2.5rem] leading-[2rem] md:leading-[2.75rem] lg:leading-[3rem] font-bold"
          >
            Enter amount
          </div>
          <br />

          <div class="flex flex-row items-baseline mt-[16px]">
            <div
              class="text-[2.25rem] md:text-[3.25rem] lg:text-[3.75rem] leading-[2.5rem] md:leading-[3.5rem] lg:leading-[4rem] font-black tracking-[-1.08px]"
              :class="toggleColor ? 'text-[#554638]' : 'text-[#A5A4A3]'"
            >
              $
            </div>
            <AmountInput
              ref="amountInput"
              v-model="selectData.givingAmount"
              :input-style="inputStyle"
              :placeholder="amountPlaceholder"
              :disabled="!showSelect"
              :class="{ 'caret-transparent': showSuccessAnimation }"
              disabled-classes="disabled:text-[#554638] disabled:bg-[#F6F4F0]"
              @click="clearAmountPlaceholder"
              @blur="checkAmount"
              @input="handleAmountInput"
            />
            <Toast safe-area />
          </div>

          <Divider :thickness="1" color="#63615E" class="pb-[0px]" />

          <p
            v-if="amountError"
            class="text-[#EC4E20] text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.125rem] italic pb-[8px]"
          >
            Please enter a number
          </p>
          <p
            v-if="amountLimitError"
            class="text-[#EC4E20] text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.125rem] italic pb-[8px]"
          >
            {{ amountErrorMessage }}
          </p>

          <ion-row
            v-if="availableLimitForSelectedAccount > 0"
            class="ion-no-padding mb-[32px] mt-[8px]"
          >
            <ion-col class="ion-no-padding">
              <small
                class="text-[#3D3A35] text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.125rem] pb-[8px]"
              >
                Transaction limit: ${{ availableLimitForSelectedAccount }}
              </small>
            </ion-col>
            <ion-col class="ion-no-padding" size="auto">
              <img
                @click="showImportantNote = true"
                src="/assets/icons/my-giving/info.svg"
                class="w-[16px] h-[16px]"
              />
            </ion-col>
          </ion-row>

          <!-- Frequency Selection -->
          <div :class="{ 'mt-[32px]': availableLimitForSelectedAccount <= 0 }">
            <BaseSelect
              v-if="!isFromGivenNow && !isEdit"
              v-model="selectData.recurringGivingSelect"
              class="w-full mb-[24px]"
              :options="isEdit ? editGivingOptions : givingOptions"
              label="Giving Frequency"
              placeholder="Select a Type"
              width-classes="border-1 w-full border-[#C3C2C1] bg-[#FFFFFF] !h-full !p-[16px] mt-[8px] md:mt-[12px] lg:mt-[16px]"
              label-classes="w-full text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.125rem] font-semibold text-[#191817]"
              text-classes="w-full text-[1rem] md:text-[1.75rem] lg:text-[1.875rem] leading-[1.5rem] md:leading-[2.375rem] lg:leading-[2.5rem] font-normal text-[#21201E]"
            />
            <div v-if="isFromGivenNow || isEdit">
              <p
                class="w-full text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.125rem] font-semibold text-[#191817]"
              >
                Giving Frequency
              </p>
              <div class="flex flex-row justify-between gap-[8px] mb-[24px] mt-[8px]">
                <label
                  class="frequency-label"
                  :class="{ active: selectData.recurringGivingSelect === 'Monthly' }"
                >
                  <input
                    class="frequency-radio"
                    type="radio"
                    value="Monthly"
                    v-model="selectData.recurringGivingSelect"
                  />
                  Monthly
                </label>
                <label
                  class="frequency-label"
                  :class="{ active: selectData.recurringGivingSelect === 'Weekly' }"
                >
                  <input
                    class="frequency-radio"
                    type="radio"
                    value="Weekly"
                    v-model="selectData.recurringGivingSelect"
                  />
                  Weekly
                </label>
              </div>
            </div>
          </div>

          <section v-if="showSelect">
            <BaseSelect
              v-if="selectData.recurringGivingSelect === 'One-Time' && showMonthSelect"
              v-model="selectData.monthOfGiving"
              class="w-full mb-[24px]"
              :options="monthOptions"
              label="Month of Giving"
              placeholder="Select a Month"
              width-classes="border-1 w-full border-[#C3C2C1] bg-[#FFFFFF] !h-full !p-[16px] mt-[8px] md:mt-[12px] lg:mt-[16px]"
              label-classes="w-full text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.125rem] font-semibold text-[#191817]"
              text-classes="w-full text-[1rem] md:text-[1.75rem] lg:text-[1.875rem] leading-[1.5rem] md:leading-[2.375rem] lg:leading-[2.5rem] font-normal text-[#21201E]"
            />

            <BaseSelect
              v-if="selectData.recurringGivingSelect === 'Weekly'"
              v-model="selectData.dayOfGiving"
              class="w-full mb-[24px]"
              :options="dayOptions"
              label="Day"
              placeholder="Select a Day"
              width-classes="border-1 w-full border-[#C3C2C1] bg-[#FFFFFF] !h-full !p-[16px] mt-[8px] md:mt-[12px] lg:mt-[16px]"
              label-classes="w-full text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.125rem] font-semibold text-[#191817]"
              text-classes="w-full text-[1rem] md:text-[1.75rem] lg:text-[1.875rem] leading-[1.5rem] md:leading-[2.375rem] lg:leading-[2.5rem] font-normal text-[#21201E]"
            />

            <div v-if="selectData.recurringGivingSelect === 'Monthly'">
              <div class="grid grid-cols-1 gap-1 mb-[24px]">
                <StartDate
                  :start-date-placeholder-text="startDatePlaceholderText"
                  :start-date="selectedStartRGDate"
                  @handle-date-data="updateStartDate"
                />
                <p
                  v-if="[29, 30, 31].includes(new Date(selectedStartRGDate).getDate())"
                  class="text-[#EC4E20] text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.125rem] italic pb-[8px]"
                >
                  For months with less than {{ new Date(selectedStartRGDate).getDate() }} days,
                  transaction will happen on the last day of the month
                </p>
              </div>
            </div>

            <div v-if="showPIDfields">
              <DateOfBirth
                class="mb-[24px]"
                :dob-placeholder-text="dobPlaceholderText"
                :original-payload="originalPayload"
                :disabled="isDateOfBirthDisabled"
                @handle-form-data="updateDateOfBirth"
              />
              <PartialNRIC
                :partial-nric-placeholder-text="partialNricPlaceholderText"
                :original-payload="originalPayload"
                :disabled="isPartialNricDisabled"
                @on-info="showInfoModal = true"
                @handle-form-data="updatePartialNric"
              />
            </div>

            <div class="w-full mt-[60px] mb-[32px] max-w-[600px] mx-auto">
              <SlideToConfirm
                ref="slideToConfirm"
                :loading="isSlideLoading"
                :error="slideError"
                :disabled="isButtonDisabled"
                :text="slideText"
                @confirm="handleSlideConfirm"
                @clear-error="slideError = false"
                class="w-full"
              />
              <p
                class="mt-[12px] mb-[12px] text-center text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.125rem] font-normal text-[#63615e] tracking-[-0.14px]"
              >
                Your transaction is secured & protected.
              </p>
            </div>
            <div v-if="isEdit && !hasChanges" class="pb-[48px]"></div>
          </section>
        </div>
      </div>
    </div>
  </my-giving-layout>
</template>

<script>
import BackButton from '@/components/common/BackButton.vue';
import AmountInput from '@/components/MyGiving/Common/AmountInput.vue';
import { sanitizeAmountInput } from '@/utils/amount-input';
import DateOfBirth from '@/components/MyGiving/Common/DateOfBirth.vue';
import StartDate from '@/components/MyGiving/Common/StartDate.vue';
import PartialNRIC from '@/components/MyGiving/Common/PartialNRIC.vue';
import Divider from '@/components/design/Divider.vue';
import BaseSelect from '@/components/design/BaseSelect.vue';
import SuccessAnimation from '@/components/common/SuccessAnimation.vue';
import SlideToConfirm from '@/components/common/SlideToConfirm.vue';
import Loading from '@/components/common/LoadingState';
import InfoModal from '@/components/MyGiving/Common/InfoModal.vue';
import Modal from '@/components/common/Modal.vue';
import { mapState, mapGetters } from 'vuex';
import SuccessModal from '@/components/MyGiving/Common/SuccessModal.vue';
import { IonChip, IonCol, IonRow } from '@ionic/vue';
import ImportantNote from '@/components/MyGiving/Common/ImportantNote.vue';
import PinOverlay from '@/components/MyGiving/Common/Pin.vue';
import MyGivingLayout from '@/components/MyGivingLayout.vue';
import Toast from '@/components/common/Toast.vue';
import { BANK_ENUM } from '@/constants/MyGiving/bank-list';
import { isIosNativeOnly } from '@/utils/platform-check';
import { bfFastSubmittedPath, playsBfGiftVideo } from '@/utils/building-fund-pledge';
import { replaceWithoutTransition } from '@/utils/replace-without-transition';
import {
  FAST_MANDATE_MISSING_MESSAGE,
  fastStatusPath,
  hasMandate,
} from '@/constants/MyGiving/fast-transfer';
import {
  clearFastSelection,
  findRestorableAccount,
  loadFastSelection,
} from '@/utils/fast-selection';
import { Keyboard } from '@capacitor/keyboard';

const TOMORROW = new Date();
TOMORROW.setDate(TOMORROW.getDate() + 1);

const MONTH_OPTIONS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const GIVING_OPTIONS = ['One-Time', 'Monthly', 'Weekly'];
const EDIT_GIVING_OPTIONS = ['Monthly', 'Weekly'];
const DAY_OPTIONS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default {
  // Ionic's router, for the Building Fund gift's swap with no page slide. Only an
  // <ion-router-outlet> provides it, hence the default.
  inject: {
    navManager: { default: null },
  },
  components: {
    PinOverlay,
    BackButton,
    AmountInput,
    DateOfBirth,
    StartDate,
    PartialNRIC,
    Divider,
    BaseSelect,
    SuccessAnimation,
    SlideToConfirm,
    InfoModal,
    SuccessModal,
    Modal,
    ImportantNote,
    Loading,
    IonChip,
    IonCol,
    IonRow,
    MyGivingLayout,
    Toast,
  },
  data() {
    return {
      // UI State
      showInfoModal: false,
      showSuccessModal: false,
      showConfirmationModal: false,
      showImportantNote: false,
      showMonthSelect: true,
      showPIDfields: true,
      showSelect: true,
      toggleColor: false,
      isLoading: false,
      isEdit: false,
      updateAction: false,
      showDeleteModal: false,

      // Form Data
      selectData: {
        givingAmount: null,
        monthOfGiving: null,
        recurringGivingSelect: 'Monthly',
        dayOfGiving: 'Sunday',
      },
      dateOfBirth: null,
      partialNric: null,
      recurringId: null,
      selectedStartRGDate: TOMORROW.toISOString(),

      // Validation
      amountError: false,
      amountLimitError: false,
      amountErrorMessage: '',
      amountPlaceholder: Number(0).toFixed(2),

      // Initial form data for change detection
      initialFormData: null,

      // Placeholders
      dobPlaceholderText: 'Enter your Date of Birth',
      partialNricPlaceholderText: 'Enter last 4 Char of NRIC / FIN',
      startDatePlaceholderText: 'Select a Start Date',

      // Options
      monthOptions: MONTH_OPTIONS,
      givingOptions: GIVING_OPTIONS,
      editGivingOptions: EDIT_GIVING_OPTIONS,
      dayOptions: DAY_OPTIONS,

      // PIN & Security
      isChecking: true,
      gateOpen: true,
      hideOverlay: true,
      overlayMode: 'verify',
      attemptsLeft: 3,
      lockedUntil: null,
      lockTimer: null,
      slideError: false,
      isSlideLoading: false,

      // Success Animation
      showSuccessAnimation: false,
      autostartAnimation: true,
      transactionIdToRedirect: null,
      spinnerPosition: { x: '50%', y: '50%' },

      // Transaction
      egiroTransactionStatus: { success: false, message: '' },
    };
  },
  computed: {
    ...mapState('myGiving', [
      'selectedGiveType',
      'selectedGiveMethod',
      'selectedLinkedAccount',
      'availableLimitForSelectedAccount',
      'eGiroMandateId',
      'isFromGivenNow',
      'linkedAccounts',
    ]),
    ...mapState('user', ['token']),
    ...mapState('profile', {
      memberId: state => state.id,
    }),
    ...mapGetters({
      originalPayload: 'profile/payload',
      fullName: 'profile/fullName',
      giveNowAmount: 'myGiving/giveNowAmount',
    }),

    giveMethodLabel() {
      return this.selectedGiveMethod;
    },
    selectedBankCode() {
      const selectedAccount = this.linkedAccounts?.find(
        account => account.eGiroMandateId === this.eGiroMandateId,
      );
      return selectedAccount?.bankCode || '';
    },
    inputStyle() {
      return {
        'background-color': '#F6F4F0',
        'border-color': '#F6F4F0',
        'font-size': '36px',
        'line-height': '40px',
        'font-weight': '900',
        'font-color': '#554638',
        'font-family': 'Inter',
      };
    },
    isFastTransfer() {
      return this.selectData.recurringGivingSelect === 'One-Time';
    },
    showPinOverlay() {
      return !this.gateOpen;
    },
    slideText() {
      if (this.selectData.recurringGivingSelect === 'One-Time') return 'Slide to Give';
      if (this.isEdit) return 'Slide to Save';
      return 'Slide to Create';
    },
    hasChanges() {
      if (!this.isEdit || !this.initialFormData) return false;

      const normalizeAmount = amount => {
        if (!amount) return null;
        const cleaned = String(amount).replace(/,/g, '');
        return parseFloat(cleaned) || null;
      };

      const currentAmount = normalizeAmount(this.selectData.givingAmount);
      const initialAmount = normalizeAmount(this.initialFormData.givingAmount);

      return (
        currentAmount !== initialAmount ||
        this.selectData.recurringGivingSelect !== this.initialFormData.recurringGivingSelect ||
        this.selectedStartRGDate !== this.initialFormData.selectedStartRGDate ||
        this.selectData.dayOfGiving !== this.initialFormData.dayOfGiving ||
        this.dateOfBirth !== this.initialFormData.dateOfBirth ||
        this.partialNric !== this.initialFormData.partialNric
      );
    },
    isIos() {
      return isIosNativeOnly();
    },
    transactionStatus() {
      return this.egiroTransactionStatus.success ? 'SUCCESS' : 'FAILED';
    },
    transactionDetails() {
      const isSuccess = this.egiroTransactionStatus.success !== false;

      return {
        givingto: this.selectedGiveType,
        givingmethod: this.giveMethodLabel,
        amount: '$' + this.selectData.givingAmount,
        frequency: this.selectData.recurringGivingSelect,
        month: this.selectData.monthOfGiving,
        transactionMessage: isSuccess
          ? this.egiroTransactionStatus.message
          : this.egiroTransactionStatus.message + ' errorCode: N/A',
        transactionTime: isSuccess
          ? this.egiroTransactionStatus?.data?.createdAt
          : this.egiroTransactionStatus.time,
        transactionId: isSuccess ? undefined : 'N/A',
      };
    },
    hasDateOfBirth() {
      return !!this.originalPayload?.dateOfBirth;
    },
    hasPartialNric() {
      return !!this.originalPayload?.partialNric;
    },
    isPartialNricDisabled() {
      return this.hasPartialNric && !!this.partialNric;
    },
    isDateOfBirthDisabled() {
      return this.hasDateOfBirth && !!this.dateOfBirth;
    },
    isButtonDisabled() {
      const hasDateOfBirth = this.dateOfBirth && String(this.dateOfBirth).trim() !== '';
      const hasPartialNric = this.partialNric && String(this.partialNric).trim() !== '';
      const isPidFieldsValid = !this.showPIDfields || (hasDateOfBirth && hasPartialNric);

      const amount = this.selectData.givingAmount
        ? Number(this.selectData.givingAmount.replace(/,/g, ''))
        : 0;

      let isFrequencyFieldsValid = true;
      if (this.selectData.recurringGivingSelect === 'One-Time') {
        if (this.showMonthSelect) {
          isFrequencyFieldsValid = !!this.selectData.monthOfGiving;
        }
      } else if (this.selectData.recurringGivingSelect === 'Weekly') {
        isFrequencyFieldsValid = !!this.selectData.dayOfGiving;
      } else if (this.selectData.recurringGivingSelect === 'Monthly') {
        isFrequencyFieldsValid = !!this.selectedStartRGDate;
      }

      return (
        !this.selectData.givingAmount ||
        this.amountLimitError ||
        this.amountError ||
        amount <= 0 ||
        !this.selectData.recurringGivingSelect ||
        !isPidFieldsValid ||
        !isFrequencyFieldsValid
      );
    },
  },
  async ionViewWillEnter() {
    // Reset animation state for subsequent transactions
    this.showSuccessAnimation = false;
    this.autostartAnimation = true;

    this.selectData.recurringGivingSelect = this.isFromGivenNow ? 'Monthly' : 'One-Time';
    this.selectData.givingAmount = null;
    this.isChecking = true;
    this.gateOpen = true;
    this.hideOverlay = true;

    const query = this.$route.query;

    if (query.recurringId && String(query.recurringId).trim()) {
      this.isEdit = true;
      const { recurringId, amount, frequency, startDate, dayOfWeek } = query;
      // After a reload the store is empty; the gift being edited names its mandate.
      if (!hasMandate(this.eGiroMandateId) && hasMandate(query.egiroRef)) {
        this.$store.commit('myGiving/setEGiroMandateId', query.egiroRef);
      }
      this.recurringId = recurringId;
      this.selectData.givingAmount = amount
        ? parseFloat(amount).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : null;
      this.toggleColor = true;
      this.selectData.recurringGivingSelect = frequency;
      this.selectedStartRGDate = startDate ? startDate.split('T')[0] : null;
      this.selectData.dayOfGiving = dayOfWeek;
    } else {
      this.isEdit = false;
      this.recurringId = null;
      this.initialFormData = null;
      // No mandate (the WebView reloaded on this page): restore the account the
      // member picked if it is still linked, else nothing can be charged.
      if (!hasMandate(this.eGiroMandateId) && !(await this.restoreFastSelection())) {
        this.leaveForMissingMandate();
        return;
      }
      // The one-time pledge the member just made to this fund (BF2026), if any,
      // which they can still change. Checked as a blur would, so an amount over the
      // account's limit is flagged before the member slides.
      this.selectData.givingAmount = this.giveNowAmount;
      if (this.selectData.givingAmount) this.checkAmount();
    }

    await this.$store.dispatch('profile/fetchProfile');

    const currentMonth = new Date().toLocaleString('en-US', { month: 'long' });
    this.selectData.monthOfGiving = currentMonth;

    if (this.selectedGiveType === 'Offering' || this.selectedGiveType === 'GenerationS Fund') {
      this.showMonthSelect = false;
    }
    if (this.selectedGiveType === 'Offering') {
      this.showPIDfields = false;
    }

    const copyOriginalPayload = { ...this.originalPayload };
    this.partialNric = copyOriginalPayload?.partialNric;
    this.dateOfBirth = copyOriginalPayload?.dateOfBirth;

    if (this.isEdit) {
      this.initialFormData = {
        givingAmount: this.selectData.givingAmount,
        recurringGivingSelect: this.selectData.recurringGivingSelect,
        selectedStartRGDate: this.selectedStartRGDate,
        dayOfGiving: this.selectData.dayOfGiving,
        dateOfBirth: this.dateOfBirth,
        partialNric: this.partialNric,
      };
    }

    this.isChecking = false;
  },
  ionViewWillLeave() {
    this.autostartAnimation = false;
  },
  ionViewDidLeave() {
    this.showSuccessModal = false;
    this.showInfoModal = false;
    this.showImportantNote = false;
    this.showConfirmationModal = false;
    this.showDeleteModal = false;
    this.showSuccessAnimation = false;
    this.autostartAnimation = false;
  },
  beforeUnmount() {
    if (this.lockTimer) {
      clearTimeout(this.lockTimer);
    }
  },
  watch: {
    'selectData.recurringGivingSelect'(newVal, oldVal) {
      this.slideError = false;
      if (newVal === 'Monthly' && oldVal !== 'Monthly') {
        this.selectedStartRGDate = null;
      }
    },
    'selectData.monthOfGiving'() {
      this.slideError = false;
    },
    'selectData.dayOfGiving'() {
      this.slideError = false;
    },
  },
  methods: {
    back() {
      this.hideOverlay = true;
      this.isSlideLoading = false;
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.replace('/my-giving');
      }
    },
    captureSpinnerPosition() {
      this.$nextTick(() => {
        const slideComponent = this.$refs.slideToConfirm;
        if (slideComponent && slideComponent.$refs.handle) {
          const handleEl = slideComponent.$refs.handle;
          const rect = handleEl.getBoundingClientRect();
          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;

          this.spinnerPosition = {
            x: `${centerX}px`,
            y: `${centerY}px`,
          };
        }
      });
    },
    async handleSlideConfirm() {
      Keyboard.hide().catch(() => {});
      this.captureSpinnerPosition();
      this.slideError = false;
      this.isSlideLoading = true;

      try {
        const amountStr = this.selectData.givingAmount;
        const amount = parseFloat(amountStr.replace(/,/g, ''));

        if (isNaN(amount) || amount <= 0) {
          throw new Error('Invalid amount');
        }

        if (amount >= 1000) {
          // Keep slide locked (isSlideLoading stays true) so user cannot re-trigger
          // the transaction while PIN modal is open or while POST is in flight.
          // Slide is reset on cancel/forgot/incorrect/locked/success/failure paths.
          this.slideError = false;
          this.isChecking = false;
          this.gateOpen = false;
          this.hideOverlay = false;
        } else {
          if (this.isEdit) {
            this.showConfirmationModal = true;
            this.isSlideLoading = false;
          } else if (!hasMandate(this.eGiroMandateId)) {
            this.leaveForMissingMandate();
          } else {
            await this.createTransaction(amount);
          }
        }
      } catch (error) {
        console.error('Error in handleSlideConfirm:', error);
        this.slideError = true;
        this.isSlideLoading = false;
      }
    },
    handleReset() {
      this.selectedStartRGDate = null;
      this.selectData = {
        givingAmount: null,
        monthOfGiving: null,
        recurringGivingSelect: 'Monthly',
        dayOfGiving: 'Sunday',
      };
    },
    onSuccessModalNavigate({ path }) {
      this.$router.replace({ path });
      this.$nextTick(() => {
        this.showSuccessModal = false;
        this.handleReset();
      });
    },
    async confirmSaveChanges() {
      this.showConfirmationModal = false;
      const amountStr = this.selectData.givingAmount;
      const amount = parseFloat(amountStr.replace(/,/g, ''));
      this.updateAction = true;
      await this.editTransaction(amount);
    },
    cancelSaveChanges() {
      this.showConfirmationModal = false;
      this.isSlideLoading = false;
      this.slideError = true;
    },
    pollTransactionStatusWithDelay(transactionId) {
      const pollInterval = 1000;
      const startTime = Date.now();
      const minWaitTime = 3000;

      const poll = async () => {
        try {
          const response = await this.$store.dispatch(
            'myGiving/checkFastTransactionStatus',
            transactionId,
          );
          const statusData = response?.data || {};
          const status = statusData.status;
          const elapsed = Date.now() - startTime;

          if (status === 'SUCCESS') {
            if (this.pollingTimer) {
              clearTimeout(this.pollingTimer);
              this.pollingTimer = null;
            }
            this.isSlideLoading = false;
            this.transactionIdToRedirect = transactionId;
            // A Building Fund gift goes straight to the BF2026 "FAST - Submitted"
            // screen (the status page renders it), with no hands animation first.
            // With the celebration it opens under the building video's navy, so
            // the page slide would show through it: swap with none.
            const bfSubmitted = bfFastSubmittedPath(this.selectedGiveType, transactionId);
            if (bfSubmitted) {
              if (playsBfGiftVideo()) {
                replaceWithoutTransition(this.navManager, this.$router, bfSubmitted);
              } else {
                this.$router.replace(bfSubmitted);
              }
              return;
            }
            this.autostartAnimation = true;
            this.showSuccessAnimation = true;
            this.$nextTick(() => {
              if (this.$refs.amountInput) {
                const inputEl = this.$refs.amountInput.$refs.amountInput;
                if (inputEl) inputEl.blur();
              }
            });
          } else if (status === 'PENDING' && elapsed >= minWaitTime) {
            if (this.pollingTimer) {
              clearTimeout(this.pollingTimer);
              this.pollingTimer = null;
            }
            this.isSlideLoading = false;
            this.$router.replace(fastStatusPath(transactionId));
          } else if (status === 'PENDING') {
            this.pollingTimer = setTimeout(poll, pollInterval);
          } else if (status === 'FAILED') {
            if (this.pollingTimer) {
              clearTimeout(this.pollingTimer);
              this.pollingTimer = null;
            }
            this.isSlideLoading = false;
            this.$router.replace(fastStatusPath(transactionId));
          }
        } catch (error) {
          console.error('Error polling transaction status:', error);
          const elapsed = Date.now() - startTime;
          if (elapsed >= minWaitTime) {
            if (this.pollingTimer) {
              clearTimeout(this.pollingTimer);
              this.pollingTimer = null;
            }
            this.isSlideLoading = false;
            this.$router.replace(fastStatusPath(transactionId));
          } else {
            this.pollingTimer = setTimeout(poll, pollInterval);
          }
        }
      };

      this.pollingTimer = setTimeout(poll, pollInterval);
    },
    // The account picked on giving-options, saved for a reload (utils/fast-selection).
    // Its bank, masked number and limit come from the freshly loaded linked accounts,
    // and only an account that is still linked is restored.
    async restoreFastSelection() {
      await this.$store.dispatch('profile/fetchProfile');
      const saved = loadFastSelection(this.memberId);
      if (!saved) return false;
      await this.$store.dispatch('myGiving/getLinkedBankAccounts');
      const account = findRestorableAccount(saved, this.linkedAccounts);
      if (!account) {
        clearFastSelection();
        return false;
      }
      this.$store.commit('myGiving/setGivingType', saved.giveType);
      this.$store.commit('myGiving/setGivingMethod', account.bankCode);
      this.$store.commit('myGiving/setGivingAccount', account.maskedBankAccNumber);
      this.$store.commit('myGiving/setEGiroMandateId', account.eGiroMandateId);
      this.$store.commit('myGiving/setAvailableLimitForSelectedAccount', account.maxAmount);
      this.$store.commit('myGiving/setIsFromGivenNow', saved.isFromGivenNow);
      return true;
    },
    // Back to MyGiving to pick the account again, rather than send a gift with no
    // mandate (see FAST_MANDATE_MISSING_MESSAGE).
    leaveForMissingMandate() {
      this.isChecking = false;
      this.isSlideLoading = false;
      this.$store.dispatch('toast/error', FAST_MANDATE_MISSING_MESSAGE);
      this.$router.replace('/my-giving');
    },
    async createTransaction(amount) {
      if (this.selectData.recurringGivingSelect !== 'One-Time') {
        this.isLoading = true;
      }
      this.slideError = false;

      try {
        const formattedDate = this.selectedStartRGDate?.includes('T')
          ? this.selectedStartRGDate.split('T')[0]
          : this.selectedStartRGDate;

        const partialNricValue = this.partialNric || this.originalPayload?.partialNric;
        const formattedPartialNric = partialNricValue ? String(partialNricValue) : '';

        const dobValue = this.dateOfBirth || this.originalPayload?.dateOfBirth;
        let formattedDateOfBirth = '';
        if (dobValue) {
          const dobString = String(dobValue);
          formattedDateOfBirth = dobString.includes('T') ? dobString.split('T')[0] : dobString;
        }

        const payLoad = {
          fullName: this.fullName,
          egiroRef: this.eGiroMandateId,
          amount: amount,
          currency: 'SGD',
          frequency: this.selectData.recurringGivingSelect.toUpperCase(),
          startDate: formattedDate,
          endDate: null,
          givingCategory: this.selectedGiveType,
          paymentDetails: 'Regular monthly giving',
          dayOfWeek:
            this.selectData.recurringGivingSelect === 'Weekly' ? this.selectData.dayOfGiving : null,
          monthOfGiving: this.selectData.monthOfGiving,
          partialNric: formattedPartialNric,
          dateOfBirth: formattedDateOfBirth,
        };

        if (this.selectData.recurringGivingSelect === 'One-Time') {
          delete payLoad.frequency;
          delete payLoad.startDate;
          delete payLoad.dayOfWeek;
          delete payLoad.endDate;
        }

        if (this.selectData.recurringGivingSelect === 'Monthly') {
          delete payLoad.monthOfGiving;
          delete payLoad.dayOfWeek;
        }

        if (this.selectData.recurringGivingSelect === 'Weekly') {
          delete payLoad.monthOfGiving;
        }

        const apiEndpoint =
          this.selectData.recurringGivingSelect === 'One-Time'
            ? process.env.VUE_APP_GIVING_API_V2_URL + '/e-giro/fast'
            : process.env.VUE_APP_GIVING_API_V2_URL + '/e-giro/recurring';

        const response = await this.$store.dispatch('myGiving/createEgiroTransaction', {
          apiEndpoint,
          payLoad,
        });

        if (this.selectData.recurringGivingSelect === 'One-Time') {
          if (response?.data?.success === false) {
            this.isSlideLoading = false;
            this.slideError = true;
            this.egiroTransactionStatus = {
              success: false,
              message: response?.data?.message || 'Transaction failed',
            };
            this.showSuccessModal = true;
            return;
          }

          const transactionId = response?.data?.transactionId || response?.data?.id;
          if (!transactionId) {
            this.isSlideLoading = false;
            this.slideError = true;
            this.egiroTransactionStatus = {
              success: false,
              message: 'Transaction ID not found in response',
            };
            this.showSuccessModal = true;
            return;
          }

          this.slideError = false;
          this.pollTransactionStatusWithDelay(transactionId);
        } else {
          this.slideError = false;
          this.isLoading = false;
          this.isSlideLoading = false;
          this.egiroTransactionStatus = response.data;
          this.showSuccessModal = true;
        }
      } catch (error) {
        console.error('Error creating transaction:', error);
        if (this.selectData.recurringGivingSelect === 'One-Time') {
          this.isSlideLoading = false;
        } else {
          this.isLoading = false;
          this.isSlideLoading = false;
        }
        this.slideError = true;
        this.egiroTransactionStatus = {
          success: false,
          message: error?.message || 'Transaction failed. Please try again.',
        };
        this.showSuccessModal = true;
      }
    },
    async editTransaction(amount) {
      this.isLoading = true;
      this.isSlideLoading = true;

      const formattedDate = this.selectedStartRGDate?.includes('T')
        ? this.selectedStartRGDate.split('T')[0]
        : this.selectedStartRGDate;

      const payLoad = {
        egiroRef: this.eGiroMandateId,
        amount: amount,
        currency: 'SGD',
        frequency: this.selectData.recurringGivingSelect.toUpperCase(),
        startDate: formattedDate,
        endDate: null,
        givingCategory: this.selectedGiveType,
        paymentDetails: 'Regular monthly giving',
        dayOfWeek:
          this.selectData.recurringGivingSelect === 'Weekly' ? this.selectData.dayOfGiving : null,
      };

      if (this.selectData.recurringGivingSelect === 'Monthly') {
        delete payLoad.dayOfWeek;
      }

      const apiEndpoint =
        process.env.VUE_APP_GIVING_API_V2_URL + '/e-giro/recurring/' + this.recurringId;

      const response = await this.$store.dispatch('myGiving/updateEgiroTransaction', {
        apiEndpoint,
        payLoad,
      });
      this.isLoading = false;
      this.isSlideLoading = false;
      this.egiroTransactionStatus = response.data;
      this.showSuccessModal = true;
    },
    clearAmountPlaceholder() {
      this.amountPlaceholder = '';
      this.toggleColor = true;
    },
    handleAmountInput(event) {
      this.selectData.givingAmount = sanitizeAmountInput(event.target.value);
    },
    checkAmount() {
      this.slideError = false;

      if (
        !this.selectData.givingAmount ||
        Number(this.selectData.givingAmount.replace(/,/g, '')) <= 0
      ) {
        this.amountPlaceholder = Number(0).toFixed(2);
        this.toggleColor = false;
        return;
      }

      const cleanInput = this.selectData.givingAmount.replace(/,/g, '');

      if (isNaN(cleanInput)) {
        this.amountError = true;
        return;
      }

      if (
        typeof this.availableLimitForSelectedAccount === 'number' &&
        Number(cleanInput) > this.availableLimitForSelectedAccount
      ) {
        if (this.availableLimitForSelectedAccount !== 0) {
          this.amountLimitError = true;
          this.$store.dispatch(
            'toast/error',
            'Your amount selected for this transaction exceeds your limit.',
          );
          return;
        }
      } else {
        this.amountLimitError = false;
        this.amountErrorMessage = '';
      }

      if (cleanInput !== '' && !isNaN(cleanInput)) {
        const formattedNum = Number(cleanInput).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        this.selectData.givingAmount = formattedNum;
        this.amountError = false;
      }
      this.toggleColor = true;
    },
    updatePartialNric(partialNric) {
      this.partialNric = partialNric;
      this.slideError = false;
    },
    updateDateOfBirth(dateOfBirth) {
      this.dateOfBirth = dateOfBirth;
      this.slideError = false;
    },
    updateStartDate(startDate) {
      this.selectedStartRGDate = startDate;
      this.slideError = false;
    },
    async handleDeleteFromId() {
      this.isLoading = true;
      try {
        const apiEndpoint =
          process.env.VUE_APP_GIVING_API_V2_URL + `/e-giro/recurring/${this.recurringId}`;
        await this.$store.dispatch('myGiving/deleteEgiroTransaction', apiEndpoint);
        this.$router.push('/my-giving/remove-recurring');
      } catch (e) {
        console.error('Delete error:', e);
        this.$store.dispatch('toast/error', 'Failed to delete recurring giving');
      } finally {
        this.isLoading = false;
      }
    },
    async confirmDelete() {
      this.showDeleteModal = false;
      await this.$nextTick();
      await this.handleDeleteFromId();
    },
    async onPinVerify(pin) {
      try {
        const res = await this.$store.dispatch('myGiving/verifyPin', { pin });
        const data = res?.data || res;

        if (data?.state === 'pin_matched') {
          this.gateOpen = true;
          this.hideOverlay = true;
          this.attemptsLeft = 3;
          this.slideError = false;
          await this.processTransaction();
          return;
        }

        if (data?.state === 'pin_incorrect') {
          this.attemptsLeft = data.attemptsLeft ?? Math.max(0, this.attemptsLeft - 1);
          const message = `Wrong PIN.  You have ${this.attemptsLeft} ${
            this.attemptsLeft === 1 ? 'attempt' : 'attempts'
          } left.`;
          this.$refs.pinOverlay?.showError(message);
          this.isSlideLoading = false;
          this.slideError = true;
          return;
        }

        if (data?.state === 'pin_locked') {
          this.overlayMode = 'locked';
          this.lockedUntil = data.lockedUntil ? new Date(data.lockedUntil) : null;
          this.attemptsLeft = 0;
          this.gateOpen = false;
          this.startLockTimer();
          this.isSlideLoading = false;
          this.slideError = true;
          return;
        }

        this.$refs.pinOverlay?.showError('Something went wrong. Please try again.');
        this.isSlideLoading = false;
        this.slideError = true;
      } catch (error) {
        // The message only: the whole error carries the request body, the PIN.
        console.error('PIN verification error:', error?.message);
        this.$refs.pinOverlay?.showError('Something went wrong. Please try again.');
        this.isSlideLoading = false;
        this.slideError = true;
      }
    },
    async processTransaction() {
      const amountStr = this.selectData.givingAmount;
      const amount = parseFloat(amountStr.replace(/,/g, ''));

      if (this.isEdit) {
        this.updateAction = true;
        await this.editTransaction(amount);
      } else if (!hasMandate(this.eGiroMandateId)) {
        this.leaveForMissingMandate();
      } else {
        await this.createTransaction(amount);
      }
    },
    async handleLockExpired() {
      try {
        const state = await this.$store.dispatch('myGiving/getPinState');

        if (state?.state === 'pin_exists' || state?.state === 'pin_verified') {
          this.overlayMode = 'verify';
          this.lockedUntil = null;
          this.attemptsLeft = 3;
        } else if (state?.state === 'pin_locked') {
          this.overlayMode = 'locked';
          this.lockedUntil = state.lockedUntil ? new Date(state.lockedUntil) : null;
          this.attemptsLeft = 0;
        } else {
          this.overlayMode = 'verify';
          this.lockedUntil = null;
          this.attemptsLeft = 3;
        }
      } catch (error) {
        console.error('E-Giro: Error refreshing PIN state after lock expiration:', error);
        this.overlayMode = 'verify';
        this.lockedUntil = null;
        this.attemptsLeft = 3;
      }
    },
    startLockTimer() {
      if (!this.lockedUntil) return;

      const tick = () => {
        const ms = this.lockedUntil - new Date();
        if (ms <= 0) {
          this.handleLockExpired();
          clearTimeout(this.lockTimer);
          return;
        }
        this.lockTimer = setTimeout(tick, 1000);
      };

      clearTimeout(this.lockTimer);
      tick();
    },
    goToForgotPin() {
      this.hideOverlay = true;
      this.isSlideLoading = false;
      this.$nextTick(() => {
        this.$router.push({ path: '/my-giving/pin/forgot-pin' });
      });
    },
    getBankShortName(bankCode) {
      return (BANK_ENUM[bankCode] && BANK_ENUM[bankCode].name) || 'Bank';
    },
    formatAccountNumber(maskedAccountNumber) {
      if (!maskedAccountNumber) return '';
      const lastFourDigits = maskedAccountNumber.replace(/\D/g, '').slice(-4);
      return `${lastFourDigits}`;
    },
  },
};
</script>

<style scoped>
ion-datetime-button::part(native) {
  background-color: #fff;
  border-radius: 8px;
  height: 56px;
  width: 100%;
  border: 1px solid #c3c2c1;
  text-align: left;
}

.delete-button-container {
  width: 100%;
  padding-top: 0px;
  padding-bottom: 0px;
  margin-top: 0px;
}

.delete-button-container--ios {
  margin-top: -8px;
  padding-top: env(safe-area-inset-top, 0px);
}

.frequency-label {
  width: 100%;
  border-radius: 10px;
  border: 1px solid #a5a4a3;
  padding: 32px 12px;
  display: flex;
  align-items: center;
  font-size: 16px;
}

.frequency-label.active {
  background: rgba(232, 220, 199, 0.4);
  font-weight: 700;
  border-width: 2px;
}

.frequency-radio {
  width: 6.4px;
  height: 6.4px;
  border: 4px solid white;
  border-radius: 50%;
  background: white;
  margin-right: 10px;
  margin-left: 12px;
}

.frequency-radio:checked {
  background: #42362b;
  box-shadow: 0 0 0 4.8px #42362b;
}

.confirmation-button {
  flex: 1;
  padding: 12px 20px;
  border-radius: 100px;
  background: transparent;
  border: none;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  color: #21201e;
}

.confirmation-button.cancel {
  border: 2px solid #42362b;
}
</style>

<style>
.delete-alert .alert-head,
.delete-alert .alert-message {
  text-align: left !important;
  padding: 12px 24px !important;
}

.delete-alert .alert-head .alert-sub-title.sc-ion-alert-ios {
  margin-top: 24px;
}

.delete-alert .alert-head .alert-title {
  font-size: 24px;
  font-weight: 900;
  margin-top: 12px;
}

.delete-alert .alert-message {
  font-size: 16px;
  font-weight: 500;
}

.delete-alert .alert-button-group {
  justify-content: center;
  background-color: #f9f9f9;
  padding: 1rem;
}

.delete-alert .alert-button:last-child {
  border-right: 2px !important;
  border-top: none !important;
}

.delete-alert .alert-button-group .alert-button-role-confirm {
  border: none;
  color: #21201e;
  font-weight: 700;
  font-size: 16px;
}

.delete-alert .alert-button-group .alert-button-role-cancel span {
  border: 2px solid #42362b;
  border-radius: 24px;
  color: #21201e;
  font-weight: 700;
  font-size: 16px;
}

.delete-alert {
  --max-width: 100% !important;
  --min-height: 200px;
  --border-radius: 12px;
}

.delete-alert .alert-wrapper {
  width: 75% !important;
}

ion-alert {
  --max-width: 100%;
}
</style>
